import {
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  TableHead,
  TableBody,
  Table,
} from "@material-ui/core";
import { setBreadcrumbTitle } from "features/breadcrumb/breadcrumbSlice";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store/rootReducer";
import styled from "styled-components/macro";
import { device, toRem } from "theme/Breakpoints";
import { noSelect, shadow } from "theme/GlobalElements";
import { colors } from "theme/Theme";
import CompareCard from "./components/CompareCard";
import { Row, Column } from "./types";
import userStorage from "store/UserStorage";
import { Product } from "features/ProductReel/types";
import { selectProductsToCompareUpdate } from "./compareSlice";

interface CompareProps {}

const Compare: React.FC<CompareProps> = () => {
  const dispatch = useDispatch();

  const [commonSpecsArray, setCommonSpecsArray] = useState<Row[]>([]);
  const checkForUpdate = useSelector(selectProductsToCompareUpdate);

  const productsToCompare = userStorage.getStoredForCompare();

  useEffect(() => {
    if (productsToCompare.length !== 0) {
      const keys = Object.keys(
        productsToCompare[0].tech_specs.data.common_specs.data
      );

      let commonSpecsEntries: Row[] = [];
      keys.forEach((key) => {
        const commonSpecsEntry: Partial<Row> | Row = {
          key:
            productsToCompare[0].tech_specs.data.common_specs.data[key].title,
        };
        productsToCompare.map((item: Product, idx: number) => {
          commonSpecsEntry[`val${idx + 1}`] =
            item.tech_specs.data.common_specs.data[key]?.value;
        });
        commonSpecsEntries.push(commonSpecsEntry as Row);
      });

      setCommonSpecsArray(commonSpecsEntries);
    }
  }, []);

  useEffect(() => {
    dispatch(setBreadcrumbTitle("Сравняване"));

    return () => {
      dispatch(setBreadcrumbTitle(""));
    };
  }, []);

  const columns: Column[] = [
    { title: "", id: "key" },
    ...productsToCompare.map((item: Product, idx: number) => {
      return { title: item.title, id: `val${idx + 1}` };
    }),
  ];

  return (
    <CompareContainer>
      <Helmet>
        <title>Сравняване на климатици</title>
      </Helmet>
      <PageTitle>Сравняване</PageTitle>
      {productsToCompare.length === 0 ? (
        <NoItems>
          <Title>Няма продукти добавени за сравняване</Title>
          <SubTitle className="text-md">
            Моля добавете продукти за сравняване
          </SubTitle>
        </NoItems>
      ) : (
        <>
          <ProductsToCompare>
            {productsToCompare.map((product: Product) => {
              return <CompareCard key={product._id} product={product} />;
            })}
          </ProductsToCompare>

          <Title className="text-md">Технически характеристики</Title>
          <StyledPaper>
            <StyledTableContainer>
              <Table stickyHeader aria-label="sticky table">
                <StyledTableHead>
                  <TableRow>
                    {columns.map((item: any, idx) => (
                      <TableCell
                        key={idx}
                        align="left"
                        style={{ maxWidth: "25vw" }}
                      >
                        {item.title}
                      </TableCell>
                    ))}
                  </TableRow>
                </StyledTableHead>
                <TableBody>
                  {commonSpecsArray.map((item, idx) => {
                    return (
                      <TableRow hover role="checkbox" tabIndex={-1} key={idx}>
                        {columns.map((column: any, idx) => {
                          const value = item[column.id];
                          return (
                            <TableCell
                              key={idx}
                              align="left"
                              style={{ maxWidth: "25vw" }}
                            >
                              {value}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </StyledTableContainer>
          </StyledPaper>
        </>
      )}
    </CompareContainer>
  );
};

const CompareContainer = styled.div`
  margin: 10px;
`;

const PageTitle = styled.h3`
  font-weight: 600;
  text-align: left;
  padding: 10px;
  margin: 20px 0;
`;

const NoItems = styled.div`
  padding: 20px;
`;
const Title = styled.h3`
  text-align: center;
  color: black;
  font-weight: 600;
  ${noSelect}
  margin: 20px 0;
`;

const SubTitle = styled.p`
  margin: 0;
  color: black;
  text-align: center;
  color: ${colors.grayest};
`;

const ProductsToCompare = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 20px;
  justify-items: center;
  margin-bottom: 30px;

  @media only screen and (${device.tablet}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media only screen and (${device.laptop}) {
    grid-template-columns: repeat(3, 1fr);
    margin-bottom: 50px;
  }

  @media only screen and (${device.laptopL}) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const StyledPaper = styled(Paper)`
  max-width: 100%;

  .MuiTableRow-root {
    td {
      padding: 3px;
      font-size: ${toRem(13)};

      @media only screen and (${device.tablet}) {
        padding: 16px;
        font-size: ${toRem(16)};
        max-width: auto;
      }
    }

    .MuiTableCell-root {
      text-align: left;
      padding: 3px;
      font-size: ${toRem(13)};

      @media only screen and (${device.tablet}) {
        padding: 16px;
        font-size: ${toRem(16)};
        max-width: auto;
      }
    }
  }
`;

const StyledTableContainer = styled(TableContainer)`
  max-height: 500px;
  max-width: 100%;
`;

const StyledTableHead = styled(TableHead)`
  th {
    font-weight: 600;
    color: white;
    background-color: ${colors.dark};
    text-align: center;
  }
`;

export default Compare;
